import LoadingSpinner from '@common/src/components/LoadingSpinner';
import useQueryParams from '@common/src/hooks/useQueryParams';
import FlowsConfigureYellowhiveCoverageItem from './FlowsConfigureYellowhiveCoverageItem';
import ConfigureItemsGrid from '@flows/src/components/flows-configure/shared/ConfigureItemsGrid';
import ContentWrapper from '@flows/src/components/flows-configure/shared/ContentWrapper';
import SelectAllButton from '@flows/src/components/flows-configure/shared/SelectAllButton';
import { FLOWS_CONFIG_PATH } from '@flows/src/FlowsConfigRouter';
import { useFlowsConfigureParams } from '@flows/src/hooks/flows-configure/useFlowsConfigureParams';
import useYellowhiveFlowsConfigureSelectAllButtonProps from '@flows/src/hooks/flows-configure/useYellowhiveFlowsConfigureSelectAllButtonProps';
import { useYellowhiveFlowsConfigureStore } from '@flows/src/store/zustand/flows-configure/yellowhiveFlowsConfigureStore';
import { PartyType } from '@parties/src/types';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router';
import { useYellowhiveFlowsCoverages } from '@flows/src/api/yellowhive-flows/getYellowhiveFlowsCoverages';

const FlowsConfigureYellowhiveCoverages: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const queryparams = useQueryParams();
  const partyType = (queryparams.get('type') as PartyType) || 'CUSTOMER';
  const { flowId, insuranceId: insuranceCode } = useFlowsConfigureParams();

  const insurances = useYellowhiveFlowsConfigureStore(
    (state) => state.insurances,
  );
  const [error, setError] = useState<string | undefined>(undefined);

  const insurance = insurances.find(
    (insurance) => insurance.code === insuranceCode,
  );

  const { data: coveragesData, isLoading } = useYellowhiveFlowsCoverages(
    partyType,
    insurance ? [insurance.code] : undefined,
  );

  // Extract coverages
  const coverages = coveragesData?.items || [];
  const allCoverageCodes = coverages.map((coverage) => coverage.code);
  const mandatoryCoverageCodes = coverages
    .filter((coverage) => coverage.mandatory)
    .map((coverage) => coverage.code);

  const { handleDeselectAll, handleSelectAll } =
    useYellowhiveFlowsConfigureSelectAllButtonProps('coverages', insuranceCode);

  // Auto-select mandatory coverages by default if none selected yet
  useEffect(() => {
    if (!isLoading && coverages.length && insuranceCode) {
      const existingSelections =
        useYellowhiveFlowsConfigureStore.getState().coverages[insuranceCode];
      if (!existingSelections || existingSelections.length === 0) {
        useYellowhiveFlowsConfigureStore
          .getState()
          .setCoverages(insuranceCode, mandatoryCoverageCodes);
      }
    }
  }, [isLoading, coverages, mandatoryCoverageCodes, insuranceCode]);

  const isAllSelected = useYellowhiveFlowsConfigureStore(
    (state) =>
      (state.coverages[insuranceCode || '']?.length || 0) ===
      allCoverageCodes.length,
  );

  if (
    !insuranceCode ||
    !insurances.map(({ code }) => code).includes(insuranceCode)
  ) {
    return (
      <Redirect
        to={{
          pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances`,
          search: window.location.search,
        }}
      />
    );
  }

  const handlePrevious = () => {
    // When going back from coverages, go back to insurance selection
    history.push({
      pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances`,
      search: window.location.search,
    });
  };

  const handleNext = () => {
    const currentCoverages =
      useYellowhiveFlowsConfigureStore.getState().coverages[insuranceCode] ||
      [];

    if (currentCoverages.length === 0) {
      setError(t('Select at least one option'));
      return;
    }

    // Check if there are more insurances to process
    const currentInsuranceIndex = insurances.findIndex(
      (insurance) => insurance.code === insuranceCode,
    );
    const nextInsurance = insurances[currentInsuranceIndex + 1];

    if (nextInsurance) {
      // Go to next insurance coverages
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${nextInsurance.code}/coverages`,
        search: window.location.search,
      });
    } else {
      // All insurances processed, go to products
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/products`,
        search: window.location.search,
      });
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ContentWrapper
      onNext={handleNext}
      onPrevious={handlePrevious}
      question={t('Guarantees for', { company: insurance?.name })}
      error={error}
    >
      <ConfigureItemsGrid>
        {coverages && coverages.length > 1 && (
          <SelectAllButton
            isAllSelected={isAllSelected}
            onDeselectAll={() => handleDeselectAll(mandatoryCoverageCodes)}
            onSelectAll={() => handleSelectAll(allCoverageCodes)}
          />
        )}
        {coverages.map((coverage) => (
          <FlowsConfigureYellowhiveCoverageItem
            key={coverage.code}
            insuranceId={insuranceCode}
            isError={!!error}
            coverage={coverage}
            clearError={() => setError(undefined)}
          />
        ))}
      </ConfigureItemsGrid>
    </ContentWrapper>
  );
};

export default FlowsConfigureYellowhiveCoverages;
